/**
 * The one client of master's account config path (I-2510):
 * `/v1/account/configs`. Every setting a user or admin can set — look, tables,
 * forms, shell, modules, apps — is a key there; master resolves it over a
 * chain of layers (platform → tenant → company → appearance → account →
 * profile → application) and answers with the effective document.
 *
 * One shared reactive store per key (module level, so the shell and every
 * hosted app see the same state), a synchronous boot cache in localStorage
 * per account (`formica.config.<account>.<key>`: the effective document and,
 * where the key was read with them, its layers and locked fields) for the
 * first paint, batched reads (`GET /v1/account/configs?keys=a,b`, one refused
 * key costs the others nothing) and debounced, coalesced writes: `patch`
 * sends a JSON merge patch (RFC 7386, null removes a member) with
 * `If-Match: <version>` per key and target. A `version_conflict` reads the
 * target's version again and sends once more — only with the version read,
 * never blind: when it cannot be read, the change stays queued and the
 * conflict is surfaced. A `field_enforced` (a tenant or company locked the
 * field) reloads and surfaces the error. Reads belong to a scope (profile,
 * company, active appearance): `reloadAccountConfigs` starts a new one, and an
 * answer of an older scope — or older than a read already applied — is dropped.
 * A change decided on the state the caller just read (a learned correction)
 * is written with `retryConflict: false`: a write of its own, never merged
 * into a batch, never retried with the same patch after a conflict, and its
 * failure stays out of the handle's `error`.
 * A closing tab sends what may leave at once (`keepalive`) and keeps the rest
 * (`formica.config-queue.<account>.<key>`) for the next start.
 * Every window keeps its own store (a tile of a workspace is a whole Connect in
 * a frame): a write's answer goes to the other windows of the browser, which
 * take it like their own, and a scope change makes them read again (I-3641).
 */
import { computed, ref, shallowRef, type Ref, type ShallowRef } from "vue";
import {
  fetchWithAuth,
  getApiOrigin,
  hasAuthCredentials,
  readShellAccountId,
} from "../auth/fetch";
import {
  applyMergePatch,
  cloneJson,
  composeMergePatches,
  isJsonObject,
  mergeJson,
  stripNulls,
  type JsonObject,
} from "./mergePatch";

/** The layers of the chain (`account_config_subject_type`), plus the code default. */
export type AccountConfigSubjectType =
  | "platform"
  | "tenant"
  | "company"
  | "appearance"
  | "account"
  | "account_profile"
  | "account_application"
  | "account_device";

/** The row a write lands on: `account:7`, `account_profile:3`, `appearance:12`. */
export interface AccountConfigTarget {
  subjectType: AccountConfigSubjectType | string;
  subjectId: number;
}

/** A top-level field an earlier layer locked for every later one. */
export interface EnforcedField {
  field: string;
  subjectType: AccountConfigSubjectType | string;
  subjectId: number;
}

/** One stored layer of the chain (`?layers=1`). */
export interface AccountConfigLayer {
  subjectType: AccountConfigSubjectType | string;
  subjectId: number;
  /** The appearance layer names its appearance (`navy`, or a stored id; a built-in has subject id 0). */
  appearance?: string;
  data: JsonObject;
  enforcedFields: string[];
  version?: number;
}

/** What master answers for one key. */
export interface AccountConfigResult<T = JsonObject> {
  key: string;
  effective: T;
  /** Version of the target row, 0 when it does not exist yet. */
  version: number;
  target: AccountConfigTarget | null;
  enforced: EnforcedField[];
  layers?: AccountConfigLayer[];
}

/** Error codes of the config path (master's error `code`). */
export const ACCOUNT_CONFIG_ERROR = {
  versionConflict: "version_conflict",
  fieldEnforced: "field_enforced",
  /** A field only tenant and company layers set (`appearance.allow_personal`). */
  fieldNotAllowed: "field_not_allowed",
  unknownKey: "unknown_config_key",
  network: "network_error",
  invalidResponse: "invalid_response",
} as const;

/** A refused or failed config request: HTTP status, master's `code`, the locked field if any. */
export class AccountConfigError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, code: string, message: string, field?: string) {
    super(message || code || `HTTP ${status}`);
    this.name = "AccountConfigError";
    this.status = status;
    this.code = code;
    if (field) this.field = field;
  }
}

export interface AccountConfigPatchOptions {
  /** The row to write (`appearance:12`); default: this session's active profile layer. */
  target?: string | AccountConfigTarget;
  /** Send now instead of after the debounce (still coalesced with what is queued). */
  immediate?: boolean;
  /**
   * `false` makes this a write of its own, for a change that is decided on
   * the state the caller just read (the learned correction): it is never
   * merged into a queued or later batch, a version conflict is not retried
   * with the patch as it is (the caller reads again and decides again), and a
   * failure is only the result (`null`) — it never lands in the handle's
   * `error`, which the pages show. Sent at once, after what is in flight.
   * Default `true`.
   */
  retryConflict?: boolean;
  /**
   * Only with `retryConflict: false`: the row version the caller decided on
   * (`handle.version` read together with the layers). It is sent as
   * `If-Match` instead of the version the row has when the write finally
   * leaves — a write that waited behind others must not carry a version
   * newer than its decision. A change in between is a refusal (`null`).
   */
  ifMatch?: number;
}

export interface UseAccountConfigOptions<T> {
  /** Merged under the effective document — what consumers see before master answered. */
  fallback?: T;
  /** Read the stored layers too (`?layers=1`). */
  layers?: boolean;
  /** Load the key when it is not loaded yet (default true). */
  immediate?: boolean;
}

export interface AccountConfigHandle<T> {
  readonly key: string;
  /** The effective document: master's answer plus this tab's writes not answered yet. */
  readonly effective: Readonly<Ref<T>>;
  readonly enforced: Readonly<Ref<EnforcedField[]>>;
  /** Version of the default target row (0: no row yet). */
  readonly version: Readonly<Ref<number>>;
  /** The row a write without `target` lands on. */
  readonly target: Readonly<Ref<AccountConfigTarget | null>>;
  readonly layers: Readonly<Ref<AccountConfigLayer[] | null>>;
  /** Master answered at least once. */
  readonly loaded: Readonly<Ref<boolean>>;
  readonly loading: Readonly<Ref<boolean>>;
  readonly error: Readonly<Ref<AccountConfigError | null>>;
  /** Queues a merge patch; resolves with master's answer, null when it failed (see `error`). */
  patch(
    partial: Partial<T> | Record<string, unknown>,
    options?: AccountConfigPatchOptions,
  ): Promise<AccountConfigResult<T> | null>;
  /** Removes one top-level field of the target row, or the whole row. */
  reset(
    field?: string,
    options?: Pick<AccountConfigPatchOptions, "target">,
  ): Promise<AccountConfigResult<T> | null>;
  reload(): Promise<void>;
  /** Sends every queued patch of this key now. */
  flush(): Promise<void>;
}

/** Debounce of `patch` per key and target. */
export const ACCOUNT_CONFIG_PATCH_DELAY_MS = 300;

const CACHE_PREFIX = "formica.config.";
const QUEUE_PREFIX = "formica.config-queue.";
const WINDOWS_CHANNEL = "formica.config-windows";

type Waiter = (result: AccountConfigResult | null) => void;

interface PendingBatch {
  patch: JsonObject;
  waiters: Waiter[];
  /**
   * Refused with a version conflict whose new version could not be read: kept
   * for the next send (never sent without `If-Match`), its waiters answered.
   */
  retained?: boolean;
}

interface PendingTarget {
  /** '' for the default target, else `type:id`. */
  target: string;
  batches: PendingBatch[];
  inflight: PendingBatch | null;
  timer: ReturnType<typeof setTimeout> | null;
  running: Promise<void> | null;
}

interface ConfigEntry {
  key: string;
  /** Last effective document master answered (null: never). */
  server: JsonObject | null;
  /** Boot cache, used until master answered. */
  cached: JsonObject | null;
  /**
   * The stored layers as master last showed them (a read with layers, the
   * boot cache), with this tab's answered writes applied; `layers` adds the
   * writes still queued, as `effective` does.
   */
  serverLayers: AccountConfigLayer[] | null;
  /** A read of this scope with layers was applied (the boot cache does not count). */
  layersRead: boolean;
  effective: ShallowRef<JsonObject>;
  enforced: ShallowRef<EnforcedField[]>;
  version: Ref<number>;
  target: ShallowRef<AccountConfigTarget | null>;
  layers: ShallowRef<AccountConfigLayer[] | null>;
  loaded: Ref<boolean>;
  loading: Ref<boolean>;
  error: ShallowRef<AccountConfigError | null>;
  wantsLayers: boolean;
  /** Requests in flight (GET). */
  reads: number;
  /** Stamp of the newest answer applied (a read's or a write's). */
  applied: number;
  /** Stamp of the newest read applied; an older read is dropped as a whole. */
  readApplied: number;
  pending: Map<string, PendingTarget>;
  /** Versions of explicit targets, learned from write answers and layers. */
  targetVersions: Map<string, number>;
  /**
   * Stamp of the last write this tab made per layer (`type:id`): a read sent
   * before it still brings its layers, but not the ones written since.
   */
  layerWrites: Map<string, number>;
  /** The writes a closed tab kept (`formica.config-queue.…`) were taken up. */
  queueResumed: boolean;
}

const entries = new Map<string, ConfigEntry>();
let stampCounter = 0;
/**
 * The scope reads belong to: `reloadAccountConfigs` (a profile, company or
 * appearance switch) starts a new one; an answer of an older one is dropped.
 */
let scopeGeneration = 0;
let queuedLoads = new Set<string>();
let queuedLoadScheduled = false;
let pageHideInstalled = false;

function nextStamp(): number {
  stampCounter += 1;
  return stampCounter;
}

/* ── Storage (boot cache) ──────────────────────────────────────────────── */

/**
 * What the boot cache keeps of a key: the effective document and, where the
 * key was read with them, the layers and locked fields — a look resolves on
 * the first paint exactly as it did when master last answered.
 */
interface CachedConfig {
  effective: JsonObject;
  layers?: AccountConfigLayer[];
  enforced?: EnforcedField[];
}

/**
 * The boot cache belongs to one account (`formica.config.<account>.<key>`): a
 * shared browser never paints the previous account's look, language or
 * theme. Without a signed-in account nothing is cached.
 */
function cacheKey(key: string): string | null {
  const account = readShellAccountId();
  return account ? `${CACHE_PREFIX}${account}.${key}` : null;
}

function readCache(key: string): CachedConfig | null {
  const storageKey = cacheKey(key);
  if (!storageKey) return null;
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isJsonObject(parsed) || !isJsonObject(parsed.effective)) return null;
    const layers = parseLayers(parsed.layers);
    const enforced = parseEnforced(parsed.enforced);
    return {
      effective: parsed.effective,
      ...(layers ? { layers } : {}),
      ...(enforced.length ? { enforced } : {}),
    };
  } catch {
    return null;
  }
}

function writeCache(entry: ConfigEntry, effective: JsonObject): void {
  const storageKey = cacheKey(entry.key);
  if (!storageKey) return;
  const cached: CachedConfig = { effective };
  if (entry.layers.value) cached.layers = entry.layers.value;
  if (entry.enforced.value.length) cached.enforced = entry.enforced.value;
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(cached));
  } catch {
    /* private mode / quota — the server keeps the value */
  }
}

/** A change a closing tab could not send, kept for the next start. */
interface KeptWrite {
  /** The row (`account_profile:3`); '' when the default target was unknown. */
  target: string;
  patch: JsonObject;
}

/** Per account like the boot cache: another account never sends them. */
function queueKey(key: string): string | null {
  const account = readShellAccountId();
  return account ? `${QUEUE_PREFIX}${account}.${key}` : null;
}

function parseKeptWrites(raw: string | null | undefined): KeptWrite[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item): KeptWrite[] =>
      isJsonObject(item) && isJsonObject(item.patch)
        ? [{ target: toStringOr(item.target), patch: item.patch }]
        : [],
    );
  } catch {
    return [];
  }
}

/** Appends — another tab of the account may have kept changes of the key too. */
function keepWrites(key: string, writes: KeptWrite[]): void {
  const storageKey = queueKey(key);
  if (!storageKey || !writes.length) return;
  try {
    const storage = globalThis.localStorage;
    const kept = parseKeptWrites(storage?.getItem(storageKey));
    storage?.setItem(storageKey, JSON.stringify([...kept, ...writes]));
  } catch {
    /* private mode / quota — the boot cache still shows the change */
  }
}

function takeKeptWrites(key: string): KeptWrite[] {
  const storageKey = queueKey(key);
  if (!storageKey) return [];
  try {
    const storage = globalThis.localStorage;
    const kept = parseKeptWrites(storage?.getItem(storageKey));
    storage?.removeItem(storageKey);
    return kept;
  } catch {
    return [];
  }
}

/* ── Wire ──────────────────────────────────────────────────────────────── */

function configsUrl(): string {
  return `${getApiOrigin()}/v1/account/configs`;
}

function keyUrl(
  key: string,
  params: Record<string, string | undefined> = {},
): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(name, value);
  }
  const qs = query.toString();
  return `${configsUrl()}/${encodeURIComponent(key)}${qs ? `?${qs}` : ""}`;
}

function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toStringOr(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

/** `appearance:12` (or a target object) as the `?target=` value. */
export function formatAccountConfigTarget(
  target: string | AccountConfigTarget,
): string {
  if (typeof target === "string") return target;
  return `${target.subjectType}:${target.subjectId}`;
}

/** Parses `appearance:12`; null for anything else. */
export function parseAccountConfigTarget(
  raw: string,
): AccountConfigTarget | null {
  const match = /^([a-z_]+):(\d+)$/.exec(raw.trim());
  if (!match) return null;
  return { subjectType: match[1], subjectId: Number(match[2]) };
}

function parseTarget(raw: unknown): AccountConfigTarget | null {
  if (typeof raw === "string") return parseAccountConfigTarget(raw);
  if (!isJsonObject(raw)) return null;
  const subjectType = toStringOr(raw.subjectType ?? raw.subject_type);
  if (!subjectType) return null;
  return { subjectType, subjectId: toNumber(raw.subjectId ?? raw.subject_id) };
}

function parseEnforced(raw: unknown): EnforcedField[] {
  if (!Array.isArray(raw)) return [];
  const out: EnforcedField[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      out.push({ field: item, subjectType: "", subjectId: 0 });
      continue;
    }
    if (!isJsonObject(item)) continue;
    const field = toStringOr(item.field);
    if (!field) continue;
    out.push({
      field,
      subjectType: toStringOr(item.subjectType ?? item.subject_type),
      subjectId: toNumber(item.subjectId ?? item.subject_id),
    });
  }
  return out;
}

function stringList(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === "string")
    : [];
}

function parseLayers(raw: unknown): AccountConfigLayer[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: AccountConfigLayer[] = [];
  for (const item of raw) {
    if (!isJsonObject(item)) continue;
    const subjectType = toStringOr(
      item.subjectType ?? item.subject_type ?? item.subjectKind,
    );
    const version = item.version == null ? undefined : toNumber(item.version);
    const appearance = toStringOr(item.appearance);
    out.push({
      subjectType,
      subjectId: toNumber(item.subjectId ?? item.subject_id),
      ...(appearance ? { appearance } : {}),
      data: isJsonObject(item.data) ? item.data : {},
      enforcedFields: stringList(item.enforcedFields ?? item.enforced_fields),
      ...(version !== undefined ? { version } : {}),
    });
  }
  return out;
}

/** Reads one `<Result>` leniently; null when it is not one. */
export function parseAccountConfigResult<T = JsonObject>(
  raw: unknown,
  key?: string,
): AccountConfigResult<T> | null {
  if (!isJsonObject(raw)) return null;
  const effective = isJsonObject(raw.effective) ? raw.effective : null;
  if (!effective) return null;
  const layers = parseLayers(raw.layers);
  return {
    key: toStringOr(raw.key, key ?? ""),
    effective: effective as T,
    version: toNumber(raw.version),
    target: parseTarget(raw.target),
    enforced: parseEnforced(raw.enforced),
    ...(layers ? { layers } : {}),
  };
}

async function readError(response: Response): Promise<AccountConfigError> {
  let code = "";
  let message = "";
  let field: string | undefined;
  try {
    const body = (await response.json()) as Record<string, unknown>;
    code = toStringOr(body?.code);
    message = toStringOr(body?.message);
    field = toStringOr(body?.field) || undefined;
  } catch {
    /* no JSON body */
  }
  if (!field && code === ACCOUNT_CONFIG_ERROR.fieldEnforced && message) {
    // Master names the field in the message: … "type" …
    const quoted = /["'`„“]([a-z][a-z0-9_.]*)["'`“”]/i.exec(message);
    if (quoted) field = quoted[1];
  }
  return new AccountConfigError(
    response.status,
    code,
    message || `HTTP ${response.status}`,
    field,
  );
}

function networkError(err: unknown): AccountConfigError {
  const message = err instanceof Error ? err.message : String(err);
  return new AccountConfigError(0, ACCOUNT_CONFIG_ERROR.network, message);
}

async function getOne(
  key: string,
  params: { layers?: boolean; appearance?: string } = {},
): Promise<AccountConfigResult> {
  let response: Response;
  try {
    response = await fetchWithAuth(
      keyUrl(key, {
        layers: params.layers ? "1" : undefined,
        appearance: params.appearance,
      }),
      { cache: "no-store" },
    );
  } catch (err) {
    throw networkError(err);
  }
  if (!response.ok) throw await readError(response);
  const result = parseAccountConfigResult(
    await response.json().catch(() => null),
    key,
  );
  if (!result) {
    throw new AccountConfigError(
      response.status,
      ACCOUNT_CONFIG_ERROR.invalidResponse,
      "invalid config response",
    );
  }
  return result;
}

/**
 * One read of a key that is not stored in the shared cache — for previews
 * (`appearance`: resolve as if that appearance were active) and admin-ish
 * views. Throws `AccountConfigError`.
 */
export function fetchAccountConfig<T = JsonObject>(
  key: string,
  options: { layers?: boolean; appearance?: string } = {},
): Promise<AccountConfigResult<T>> {
  return getOne(key, options) as Promise<AccountConfigResult<T>>;
}

type ReadOutcome = AccountConfigResult | AccountConfigError;

/** One key's refusal inside a batch answer (`errors.<key>`: `{code, message}`). */
function batchKeyError(key: string, raw: unknown): AccountConfigError {
  if (!isJsonObject(raw)) {
    return new AccountConfigError(
      0,
      ACCOUNT_CONFIG_ERROR.invalidResponse,
      `the config batch did not answer for ${key}`,
    );
  }
  const code = toStringOr(raw.code);
  const status =
    toNumber(raw.status) ||
    (code === ACCOUNT_CONFIG_ERROR.unknownKey ? 404 : 400);
  return new AccountConfigError(status, code, toStringOr(raw.message));
}

/**
 * One batch request (`GET /v1/account/configs?keys=a,b[&layers=1]
 * [&appearance=<id>]`): master answers every key it resolved in `configs` and
 * every key it refused in `errors`, so one unknown key costs the others
 * nothing. A request that fails as a whole — the network, or a preview
 * appearance the session may not see (404 `unknown_appearance`) — throws.
 */
async function getMany(
  keys: readonly string[],
  params: { layers?: boolean; appearance?: string } = {},
): Promise<Map<string, ReadOutcome>> {
  let query = `keys=${keys.map((key) => encodeURIComponent(key)).join(",")}`;
  if (params.layers) query += "&layers=1";
  if (params.appearance)
    query += `&appearance=${encodeURIComponent(params.appearance)}`;
  let response: Response;
  try {
    response = await fetchWithAuth(`${configsUrl()}?${query}`, {
      cache: "no-store",
    });
  } catch (err) {
    throw networkError(err);
  }
  if (!response.ok) throw await readError(response);
  const body = (await response.json().catch(() => null)) as {
    configs?: unknown;
    errors?: unknown;
  } | null;
  const configs = isJsonObject(body?.configs) ? body.configs : {};
  const errors = isJsonObject(body?.errors) ? body.errors : {};
  const out = new Map<string, ReadOutcome>();
  for (const key of keys) {
    const result = parseAccountConfigResult(configs[key], key);
    out.set(key, result ?? batchKeyError(key, errors[key]));
  }
  return out;
}

/**
 * Reads several keys in one request (`getMany`); a request that fails as a
 * whole fails for every key.
 */
async function readKeys(
  keys: string[],
  layers: boolean,
): Promise<Map<string, ReadOutcome>> {
  const out = new Map<string, ReadOutcome>();
  if (keys.length === 1) {
    const key = keys[0]!;
    try {
      out.set(key, await getOne(key, { layers }));
    } catch (err) {
      out.set(key, err instanceof AccountConfigError ? err : networkError(err));
    }
    return out;
  }
  try {
    return await getMany(keys, { layers });
  } catch (err) {
    const error = err instanceof AccountConfigError ? err : networkError(err);
    for (const key of keys) out.set(key, error);
    return out;
  }
}

/** The most keys master resolves in one batch request. */
export const ACCOUNT_CONFIG_BATCH_MAX = 50;

/** `items` in slices master takes in one batch request. */
function batchesOf<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += ACCOUNT_CONFIG_BATCH_MAX)
    out.push(items.slice(i, i + ACCOUNT_CONFIG_BATCH_MAX));
  return out;
}

/** What `fetchAccountConfigs` read: the resolved keys and, per refused key, its refusal. */
export interface AccountConfigBatch<T = JsonObject> {
  configs: Record<string, AccountConfigResult<T>>;
  errors: Record<string, AccountConfigError>;
}

/**
 * Several keys in one read that is not stored in the shared cache — the batch
 * form of `fetchAccountConfig`, e.g. every further key of an appearance
 * (`appearance`: resolve as if it were active; `layers`: with the layers). A
 * key master refuses lands in `errors`, the others resolve; more than
 * `ACCOUNT_CONFIG_BATCH_MAX` keys go out in several requests. Throws
 * `AccountConfigError` when a request fails as a whole — a preview appearance
 * the session may not see refuses it (404 `unknown_appearance`).
 */
export async function fetchAccountConfigs<T = JsonObject>(
  keys: readonly string[],
  options: { layers?: boolean; appearance?: string } = {},
): Promise<AccountConfigBatch<T>> {
  const answers = await Promise.all(
    batchesOf([...new Set(keys)]).map((chunk) => getMany(chunk, options)),
  );
  const out: AccountConfigBatch<T> = { configs: {}, errors: {} };
  for (const answer of answers) {
    for (const [key, outcome] of answer) {
      if (outcome instanceof AccountConfigError) out.errors[key] = outcome;
      else out.configs[key] = outcome as AccountConfigResult<T>;
    }
  }
  return out;
}

/* ── Store ─────────────────────────────────────────────────────────────── */

function entryFor(key: string): ConfigEntry {
  let entry = entries.get(key);
  if (entry) return entry;
  listenToOtherWindows();
  const cached = readCache(key);
  entry = {
    key,
    server: null,
    cached: cached?.effective ?? null,
    serverLayers: cached?.layers ?? null,
    layersRead: false,
    effective: shallowRef<JsonObject>(
      cached ? cloneJson(cached.effective) : {},
    ),
    enforced: shallowRef<EnforcedField[]>(cached?.enforced ?? []),
    version: ref(0),
    target: shallowRef<AccountConfigTarget | null>(null),
    layers: shallowRef<AccountConfigLayer[] | null>(cached?.layers ?? null),
    loaded: ref(false),
    loading: ref(false),
    error: shallowRef<AccountConfigError | null>(null),
    wantsLayers: false,
    reads: 0,
    applied: 0,
    readApplied: 0,
    pending: new Map(),
    targetVersions: new Map(),
    layerWrites: new Map(),
    queueResumed: false,
  };
  entries.set(key, entry);
  return entry;
}

/** The place of a layer's subject in the chain (`account_config_subject_type`). */
const CHAIN_ORDER: readonly string[] = [
  "platform",
  "tenant",
  "company",
  "appearance",
  "account",
  "account_profile",
  "account_application",
  "account_device",
];

function chainRank(subjectType: string): number {
  const rank = CHAIN_ORDER.indexOf(subjectType);
  return rank < 0 ? CHAIN_ORDER.length : rank;
}

function layerTarget(layer: AccountConfigLayer): string {
  return `${layer.subjectType}:${layer.subjectId}`;
}

/**
 * The layers after a write to `target` that master answered without layers:
 * the written layer takes the patch (a DELETE removes it), everything else is
 * as it was — so the layers keep matching the effective document.
 */
function layersAfterWrite(
  layers: readonly AccountConfigLayer[],
  target: AccountConfigTarget,
  write: { method: "PATCH" | "DELETE"; body?: JsonObject },
  version?: number,
): AccountConfigLayer[] {
  const key = formatAccountConfigTarget(target);
  const index = layers.findIndex((layer) => layerTarget(layer) === key);
  const next = [...layers];
  if (write.method === "DELETE") {
    if (index >= 0) next.splice(index, 1);
    return next;
  }
  const before = index >= 0 ? layers[index]! : null;
  const data = applyMergePatch(before?.data ?? {}, write.body ?? {});
  const nextVersion = version ?? before?.version;
  const layer: AccountConfigLayer = {
    subjectType: target.subjectType,
    subjectId: target.subjectId,
    ...(before?.appearance ? { appearance: before.appearance } : {}),
    data: isJsonObject(data) ? data : {},
    enforcedFields: before?.enforcedFields ?? [],
    ...(nextVersion !== undefined ? { version: nextVersion } : {}),
  };
  if (!Object.keys(layer.data).length && !layer.enforcedFields.length) {
    // Master deletes a layer a patch leaves empty.
    if (index >= 0) next.splice(index, 1);
    return next;
  }
  if (index >= 0) {
    next[index] = layer;
    return next;
  }
  const rank = chainRank(target.subjectType);
  const at = next.findIndex((item) => chainRank(item.subjectType) > rank);
  next.splice(at < 0 ? next.length : at, 0, layer);
  return next;
}

/**
 * The layers of a read that was sent before a write of this tab and answered
 * after it: its layers are newer than what the entry holds, except the ones
 * this tab wrote since the read was sent — those stay as the entry has them.
 */
function mergeStaleLayers(
  entry: ConfigEntry,
  layers: readonly AccountConfigLayer[],
  stamp: number,
): AccountConfigLayer[] {
  const writtenSince = (target: string) =>
    (entry.layerWrites.get(target) ?? 0) > stamp;
  const own = new Map(
    (entry.serverLayers ?? []).map((layer) => [layerTarget(layer), layer]),
  );
  const merged: AccountConfigLayer[] = [];
  for (const layer of layers) {
    const target = layerTarget(layer);
    if (!writtenSince(target)) merged.push(layer);
    else if (own.has(target)) merged.push(own.get(target)!);
  }
  for (const [target, layer] of own) {
    if (writtenSince(target) && !layers.some((l) => layerTarget(l) === target))
      merged.push(layer);
  }
  return merged.sort(
    (a, b) => chainRank(a.subjectType) - chainRank(b.subjectType),
  );
}

/**
 * Master's document plus the default-target writes it has not answered yet —
 * and the same for the layers, so both keep telling the same story.
 */
function recompute(entry: ConfigEntry): void {
  let doc: unknown = entry.server ?? entry.cached ?? {};
  let layers = entry.serverLayers;
  const own = entry.pending.get("");
  const queued = own
    ? [
        ...(own.inflight ? [own.inflight.patch] : []),
        ...own.batches.map((batch) => batch.patch),
      ]
    : [];
  const target = entry.target.value;
  for (const patch of queued) {
    doc = applyMergePatch(doc, patch);
    if (layers && target)
      layers = layersAfterWrite(layers, target, {
        method: "PATCH",
        body: patch,
      });
  }
  const next = isJsonObject(doc) ? doc : {};
  entry.effective.value = next;
  entry.layers.value = layers;
  if (queued.length) {
    // The user's last choice paints the next start even if the write is lost.
    writeCache(entry, next);
  }
}

function sameTarget(
  a: AccountConfigTarget | null,
  b: AccountConfigTarget | null,
): boolean {
  return Boolean(
    a && b && a.subjectType === b.subjectType && a.subjectId === b.subjectId,
  );
}

function rememberLayerVersions(
  entry: ConfigEntry,
  layers: AccountConfigLayer[] | undefined,
): void {
  if (!layers) return;
  for (const layer of layers) {
    if (
      layer.version !== undefined &&
      layer.subjectType &&
      layer.subjectId > 0
    ) {
      entry.targetVersions.set(
        `${layer.subjectType}:${layer.subjectId}`,
        layer.version,
      );
    }
  }
}

/**
 * Takes master's answer for the default target (or a read). An answer
 * without layers (a write's) keeps the layers the entry holds — the caller
 * has already applied the write to them.
 */
function applyServerResult(
  entry: ConfigEntry,
  result: AccountConfigResult,
): void {
  entry.server = isJsonObject(result.effective)
    ? (result.effective as JsonObject)
    : {};
  entry.version.value = result.version;
  entry.target.value = result.target;
  entry.enforced.value = result.enforced;
  if (result.layers) {
    entry.serverLayers = result.layers;
    entry.layersRead = true;
    rememberLayerVersions(entry, result.layers);
  }
  if (result.target)
    entry.targetVersions.set(
      formatAccountConfigTarget(result.target),
      result.version,
    );
  entry.loaded.value = true;
  entry.error.value = null;
  // Master's first answer names the default target the kept changes belong to.
  if (!entry.queueResumed) resumeKeptWrites(entry);
  recompute(entry);
  writeCache(entry, entry.effective.value);
}

/**
 * Reads the keys of `list` — in batches of at most `ACCOUNT_CONFIG_BATCH_MAX`,
 * each key's state (loading, answer, error) on its own — and resolves with the
 * entries whose answer was applied. An answer of an older scope, or older than
 * a read already applied to the key, is dropped.
 */
async function loadEntries(list: ConfigEntry[]): Promise<Set<ConfigEntry>> {
  const applied = new Set<ConfigEntry>();
  if (!list.length || !hasAuthCredentials()) return applied;
  const stamp = nextStamp();
  const generation = scopeGeneration;
  await Promise.all(
    batchesOf(list).map((batch) =>
      loadBatch(batch, stamp, generation, applied),
    ),
  );
  return applied;
}

async function loadBatch(
  list: ConfigEntry[],
  stamp: number,
  generation: number,
  applied: Set<ConfigEntry>,
): Promise<void> {
  for (const entry of list) {
    entry.reads += 1;
    entry.loading.value = true;
  }
  const withLayers = list.some((entry) => entry.wantsLayers);
  let outcomes: Map<string, ReadOutcome>;
  try {
    outcomes = await readKeys(
      list.map((entry) => entry.key),
      withLayers,
    );
  } finally {
    for (const entry of list) {
      entry.reads -= 1;
      entry.loading.value = entry.reads > 0;
    }
  }
  // The keys whose answer was not used: an old scope's, or older than a read
  // applied since (a stale failure included — that newer read brought no layers).
  const discarded = new Set<ConfigEntry>();
  // Sent before a profile, company or appearance switch: the answer is the
  // old scope's — none of it (document, layers, locks, error) applies.
  if (generation !== scopeGeneration) {
    for (const entry of list) discarded.add(entry);
  } else {
    for (const entry of list) {
      const outcome = outcomes.get(entry.key);
      if (!outcome) continue;
      // A newer read of the key is in already: this answer is older in every part.
      if (stamp < entry.readApplied) {
        discarded.add(entry);
        continue;
      }
      if (outcome instanceof AccountConfigError) {
        entry.error.value = outcome;
        continue;
      }
      entry.readApplied = stamp;
      // Only the keys that asked for them keep layers (the batch brings them for all).
      const result: AccountConfigResult = { ...outcome };
      if (!entry.wantsLayers) delete result.layers;
      if (stamp < entry.applied) {
        // A write of this tab was answered while this read was on its way: its
        // document is older, but its layers are the newest there are — except
        // the ones written since.
        if (result.layers) {
          entry.serverLayers = mergeStaleLayers(entry, result.layers, stamp);
          entry.layersRead = true;
          rememberLayerVersions(entry, entry.serverLayers);
          recompute(entry);
          writeCache(entry, entry.effective.value);
        }
        continue;
      }
      entry.applied = stamp;
      applyServerResult(entry, result);
      applied.add(entry);
    }
  }
  // A handle asked for layers and none were delivered: this read was sent
  // without them, or its answer for the key was discarded (an old scope's, or
  // older than a read applied since). Read once more, now with layers — in the
  // current scope, by the last read of the key in flight, and not when a read
  // with layers was applied already. A layered answer of this scope that failed
  // or is missing is not read again (one that a scope switch discarded is), and
  // the queued read asks for layers, so it never loops.
  // `wantsLayers` is the entry's, so a handle with `immediate: false` is served too.
  if (!hasAuthCredentials()) return;
  for (const entry of list) {
    if (!entry.wantsLayers || entry.layersRead || entry.reads > 0) continue;
    if (!withLayers || discarded.has(entry)) queueLoad(entry.key);
  }
}

function flushQueuedLoads(): void {
  queuedLoadScheduled = false;
  const keys = [...queuedLoads];
  queuedLoads = new Set();
  void loadEntries(keys.map((key) => entryFor(key)));
}

/** Loads a key in the next microtask, together with every other key asked for in the same tick. */
function queueLoad(key: string): void {
  queuedLoads.add(key);
  if (queuedLoadScheduled) return;
  queuedLoadScheduled = true;
  queueMicrotask(flushQueuedLoads);
}

/**
 * Batch prefetch (one request): the shell calls it after `/oauth/userinfo`
 * for the keys it paints with. Never throws; failures land in each key's
 * `error`, the boot cache stays in place.
 */
export async function loadAccountConfigs(
  keys: readonly string[],
): Promise<void> {
  const unique = [
    ...new Set(keys.filter((key) => typeof key === "string" && key)),
  ];
  await loadEntries(unique.map((key) => entryFor(key)));
}

/**
 * Reloads every key this tab has loaded — after a profile, company or
 * appearance switch — and tells the other windows, which all work in the same
 * session, to do the same.
 */
export async function reloadAccountConfigs(): Promise<void> {
  const reloaded = startNewScope();
  tellOtherWindows({ scopeChanged: true });
  await reloaded;
}

/**
 * Reads this tab's keys again in a new scope: reads still on their way belong
 * to the old one and are dropped when they arrive. A change kept back after an
 * unresolved version conflict on the session's own layer goes too — that
 * layer may be another one now, and the conflict was surfaced.
 */
async function startNewScope(): Promise<void> {
  scopeGeneration += 1;
  for (const entry of entries.values()) entry.layersRead = false;
  const loaded = [...entries.values()].filter(
    (entry) => entry.loaded.value || entry.reads > 0,
  );
  for (const entry of entries.values()) {
    const own = entry.pending.get("");
    if (!own?.batches.some((batch) => batch.retained)) continue;
    own.batches = own.batches.filter((batch) => !batch.retained);
    recompute(entry);
  }
  await loadEntries(loaded);
}

/* ── Other windows ─────────────────────────────────────────────────────── */

type WriteRequest = { method: "PATCH" | "DELETE"; body?: JsonObject };

/** What a window tells the others of the browser. */
type WindowsMessage =
  | { key: string; result: AccountConfigResult; write: WriteRequest }
  | { scopeChanged: true };

let windowsChannel: BroadcastChannel | null = null;

function listenToOtherWindows(): void {
  if (windowsChannel || typeof BroadcastChannel === "undefined") return;
  try {
    windowsChannel = new BroadcastChannel(WINDOWS_CHANNEL);
    windowsChannel.onmessage = (event: MessageEvent) => {
      const { account, message } = event.data as {
        account: string;
        message: WindowsMessage;
      };
      if (account === readShellAccountId()) hearOtherWindow(message);
    };
  } catch {
    windowsChannel = null;
  }
}

function tellOtherWindows(message: WindowsMessage): void {
  const account = readShellAccountId();
  if (!account) return;
  try {
    windowsChannel?.postMessage({ account, message });
  } catch {
    // The other windows catch up with their next read.
  }
}

/**
 * A key this window has not read is read when it is needed. A write of the row
 * this window works on is applied like its own; anything else (another row, a
 * reset that can remove the row and with it the version) is read again.
 */
function hearOtherWindow(message: WindowsMessage): void {
  if ("scopeChanged" in message) {
    void startNewScope();
    return;
  }
  const entry = entries.get(message.key);
  if (!entry || (!entry.loaded.value && entry.reads === 0)) return;
  const { result, write } = message;
  if (
    entry.loaded.value &&
    write.method === "PATCH" &&
    sameTarget(result.target, entry.target.value)
  ) {
    if (result.version > entry.version.value)
      afterWrite(entry, "", result, write, scopeGeneration);
    return;
  }
  queueLoad(entry.key);
}

/* ── Writes ────────────────────────────────────────────────────────────── */

function pendingFor(entry: ConfigEntry, target: string): PendingTarget {
  let pending = entry.pending.get(target);
  if (!pending) {
    pending = {
      target,
      batches: [],
      inflight: null,
      timer: null,
      running: null,
    };
    entry.pending.set(target, pending);
  }
  return pending;
}

function defaultTargetOf(entry: ConfigEntry): string {
  return entry.target.value
    ? formatAccountConfigTarget(entry.target.value)
    : "";
}

/**
 * The version a write to `target` expects (`If-Match`); null while this tab
 * does not know it. A known 0 is sent too: it guards the write that creates
 * the row.
 */
function ifMatchVersion(entry: ConfigEntry, target: string): number | null {
  if ((!target || target === defaultTargetOf(entry)) && entry.loaded.value)
    return entry.version.value;
  if (!target) return null;
  return entry.targetVersions.get(target) ?? null;
}

/**
 * After a version conflict: reads the version the row of `target` is at now,
 * so the one retry sends a true `If-Match`. The default target comes with a
 * reload; another row (an appearance, the account behind a profile) from its
 * layer in master's layers — an appearance's through the preview of that
 * appearance. A row that is gone is at 0. Resolves false when the version
 * could not be read: then there is no retry.
 */
async function refreshTargetVersion(
  entry: ConfigEntry,
  target: string,
): Promise<boolean> {
  entry.targetVersions.delete(target);
  if (!target || target === defaultTargetOf(entry)) {
    const applied = await loadEntries([entry]);
    return applied.has(entry);
  }
  const subject = parseAccountConfigTarget(target);
  if (!subject) return false;
  try {
    const result = await getOne(entry.key, {
      layers: true,
      ...(subject.subjectType === "appearance"
        ? { appearance: String(subject.subjectId) }
        : {}),
    });
    const layer = result.layers?.find(
      (item) =>
        item.subjectType === subject.subjectType &&
        item.subjectId === subject.subjectId,
    );
    if (!layer) entry.targetVersions.set(target, 0);
    else if (layer.version !== undefined)
      entry.targetVersions.set(target, layer.version);
    else return false;
    return true;
  } catch (cause) {
    entry.error.value =
      cause instanceof AccountConfigError ? cause : networkError(cause);
    return false;
  }
}

function afterWrite(
  entry: ConfigEntry,
  target: string,
  result: AccountConfigResult,
  write: WriteRequest,
  generation: number,
): void {
  const answeredTarget = result.target
    ? formatAccountConfigTarget(result.target)
    : target;
  entry.targetVersions.set(answeredTarget, result.version);
  const stamp = nextStamp();
  if (answeredTarget) entry.layerWrites.set(answeredTarget, stamp);
  const isDefault = !target || sameTarget(result.target, entry.target.value);
  if (isDefault && generation === scopeGeneration) {
    // The answer carries no layers: the written one takes the write here, so
    // the layers keep matching the effective document.
    if (!result.layers && entry.serverLayers && result.target) {
      entry.serverLayers = layersAfterWrite(
        entry.serverLayers,
        result.target,
        write,
        result.version,
      );
    }
    entry.applied = stamp;
    applyServerResult(entry, result);
    return;
  }
  // Another row changed (an appearance, the account behind a profile) — or
  // the scope did while the write was on its way: what this session sees may
  // have changed with it — ask master. The answer stays the caller's alone:
  // master answers a write of an appearance's layer with the preview of that
  // appearance, never the session's document, so it must not reach the shared
  // state or the boot cache.
  void loadEntries([entry]);
}

/**
 * A version conflict whose row version could not be read again: the write is
 * not sent once more without `If-Match` — it stays queued.
 */
const CONFLICT_UNRESOLVED = Symbol("conflict_unresolved");

type SendOutcome = AccountConfigResult | null | typeof CONFLICT_UNRESOLVED;

interface SendOptions {
  /** Read the version and send once more after a version conflict. */
  retry: boolean;
  /** A failure is the caller's alone: the shared `error` of the key stays as it is. */
  quiet?: boolean;
  /** The version the change was decided on, sent as `If-Match` as it is. */
  ifMatch?: number;
  /** A change kept back after a conflict: sent only with a version read. */
  requireVersion?: boolean;
  /** The page is closing: the request outlives it (unanswered then). */
  keepalive?: boolean;
  /**
   * Master answered for good (taken or refused): the caller drops the change
   * from what it still shows, before the answer becomes the state — and the
   * boot cache — so the change is not applied over it once more.
   */
  settled?: () => void;
}

async function send(
  entry: ConfigEntry,
  target: string,
  init: WriteRequest,
  options: SendOptions,
): Promise<SendOutcome> {
  const generation = scopeGeneration;
  // Master guards writes with If-Match; a reset (DELETE) takes none.
  let version =
    init.method === "PATCH"
      ? (options.ifMatch ?? ifMatchVersion(entry, target))
      : null;
  if (
    (options.requireVersion || target) &&
    init.method === "PATCH" &&
    version === null
  ) {
    // An explicit target can be another layer than this session's profile.
    // Its first write needs that row's version too, including 0 for no row.
    if (await refreshTargetVersion(entry, target))
      version = ifMatchVersion(entry, target);
    if (version === null) {
      entry.error.value ??= new AccountConfigError(
        0,
        ACCOUNT_CONFIG_ERROR.invalidResponse,
        "The target config version is unavailable.",
      );
      return options.requireVersion ? CONFLICT_UNRESOLVED : null;
    }
  }
  const headers: Record<string, string> = {};
  if (init.body) headers["Content-Type"] = "application/json";
  if (version !== null) headers["If-Match"] = String(version);
  let response: Response;
  try {
    response = await fetchWithAuth(
      keyUrl(entry.key, { target: target || undefined }),
      {
        method: init.method,
        headers,
        ...(init.body ? { body: JSON.stringify(init.body) } : {}),
        ...(options.keepalive ? { keepalive: true } : {}),
      },
    );
  } catch (err) {
    if (!options.quiet) entry.error.value = networkError(err);
    return null;
  }
  if (response.ok) {
    const result = parseAccountConfigResult(
      await response.json().catch(() => null),
      entry.key,
    );
    options.settled?.();
    if (!result) {
      await loadEntries([entry]);
      if (!options.quiet)
        entry.error.value = new AccountConfigError(
          response.status,
          ACCOUNT_CONFIG_ERROR.invalidResponse,
          "invalid config response",
        );
      return null;
    }
    afterWrite(entry, target, result, init, generation);
    tellOtherWindows({ key: entry.key, result, write: init });
    return result;
  }
  const error = await readError(response);
  if (error.code === ACCOUNT_CONFIG_ERROR.versionConflict && options.retry) {
    // Someone else wrote the row: read its version and apply the change once
    // more — with that version only. Unread, a retry would overwrite them.
    const verified =
      (await refreshTargetVersion(entry, target)) &&
      ifMatchVersion(entry, target) !== null;
    if (verified)
      return send(entry, target, init, { ...options, retry: false });
    if (!options.quiet) entry.error.value = error;
    return CONFLICT_UNRESOLVED;
  }
  // Back to what master holds (a locked field, a refused target), then say why.
  options.settled?.();
  await loadEntries([entry]);
  if (!options.quiet) entry.error.value = error;
  return null;
}

async function drain(
  entry: ConfigEntry,
  pending: PendingTarget,
  keepalive: boolean,
): Promise<void> {
  while (pending.batches.length) {
    const batch = pending.batches.shift()!;
    const wasRetained = batch.retained === true;
    batch.retained = false;
    pending.inflight = batch;
    let outcome: SendOutcome = null;
    try {
      outcome = await send(
        entry,
        pending.target,
        { method: "PATCH", body: batch.patch },
        {
          retry: true,
          requireVersion: wasRetained,
          keepalive,
          settled: () => {
            if (pending.inflight === batch) pending.inflight = null;
          },
        },
      );
    } finally {
      pending.inflight = null;
      const answer = outcome === CONFLICT_UNRESOLVED ? null : outcome;
      // Kept for the next send (and still shown); its callers learn now.
      if (outcome === CONFLICT_UNRESOLVED)
        pending.batches.unshift({ ...batch, waiters: [], retained: true });
      recompute(entry);
      for (const waiter of batch.waiters) waiter(answer);
    }
    if (outcome === CONFLICT_UNRESOLVED) return;
  }
}

/**
 * Sends what is queued for one target, after whatever is in flight for it.
 * With nothing in flight the first request leaves synchronously — what the
 * `pagehide` flush relies on.
 */
function flushPending(
  entry: ConfigEntry,
  pending: PendingTarget,
  keepalive = false,
): Promise<void> {
  if (pending.timer) {
    clearTimeout(pending.timer);
    pending.timer = null;
  }
  if (pending.running)
    return pending.running.then(() => flushPending(entry, pending));
  if (!pending.batches.length) return Promise.resolve();
  pending.running = drain(entry, pending, keepalive).finally(() => {
    pending.running = null;
  });
  return pending.running;
}

function schedule(
  entry: ConfigEntry,
  pending: PendingTarget,
  immediate: boolean,
): void {
  if (pending.timer) clearTimeout(pending.timer);
  pending.timer = null;
  if (immediate) {
    void flushPending(entry, pending);
    return;
  }
  pending.timer = setTimeout(() => {
    pending.timer = null;
    void flushPending(entry, pending);
  }, ACCOUNT_CONFIG_PATCH_DELAY_MS);
}

/**
 * Without a session there is nobody to write to: the change applies to this
 * browser only (effective + boot cache) until master answers after a login.
 */
function applyLocally(entry: ConfigEntry, partial: JsonObject): void {
  const base = entry.server ?? entry.cached ?? {};
  const next = applyMergePatch(base, partial);
  const doc = isJsonObject(next) ? next : {};
  if (entry.server) entry.server = doc;
  else entry.cached = doc;
  recompute(entry);
  writeCache(entry, entry.effective.value);
}

/**
 * A write of its own (`retryConflict: false`): not merged into any batch, not
 * retried after a version conflict, silent in the handle's `error`. It waits
 * for the request in flight (one writer per row, so its `If-Match` is true)
 * and takes the same lane, so a queued batch leaves after it, not beside it.
 * Without a session it applies to this browser like any other patch. With
 * `options.ifMatch` it sends the version the caller decided on, not the one
 * the row has after the wait.
 */
function sendSolo(
  entry: ConfigEntry,
  partial: JsonObject,
  options: AccountConfigPatchOptions,
): Promise<AccountConfigResult | null> {
  if (!hasAuthCredentials()) {
    if (!options.target) applyLocally(entry, partial);
    return Promise.resolve(null);
  }
  const target = options.target
    ? formatAccountConfigTarget(options.target)
    : "";
  const pending = pendingFor(entry, target);
  installPageHideFlush();
  const body = cloneJson(partial);
  return (async () => {
    while (pending.running) await pending.running;
    const run = send(
      entry,
      target,
      { method: "PATCH", body },
      { retry: false, quiet: true, ifMatch: options.ifMatch },
    );
    pending.running = run
      .then(() => undefined, () => undefined)
      .finally(() => {
        pending.running = null;
      });
    const outcome = await run;
    return outcome === CONFLICT_UNRESOLVED ? null : outcome;
  })();
}

function queuePatch(
  entry: ConfigEntry,
  partial: JsonObject,
  options: AccountConfigPatchOptions = {},
): Promise<AccountConfigResult | null> {
  if (options.retryConflict === false) return sendSolo(entry, partial, options);
  if (!hasAuthCredentials()) {
    if (!options.target) applyLocally(entry, partial);
    return Promise.resolve(null);
  }
  const target = options.target
    ? formatAccountConfigTarget(options.target)
    : "";
  const pending = pendingFor(entry, target);
  installPageHideFlush();
  return new Promise((resolve) => {
    const last = pending.batches[pending.batches.length - 1];
    const composed =
      last && !last.retained ? composeMergePatches(last.patch, partial) : null;
    if (last && composed) {
      last.patch = composed;
      last.waiters.push(resolve as Waiter);
    } else {
      pending.batches.push({
        patch: cloneJson(partial),
        waiters: [resolve as Waiter],
      });
    }
    if (!target) recompute(entry);
    schedule(entry, pending, options.immediate === true);
  });
}

async function resetEntry(
  entry: ConfigEntry,
  field: string | undefined,
  options: Pick<AccountConfigPatchOptions, "target"> = {},
): Promise<AccountConfigResult | null> {
  if (field)
    return queuePatch(entry, { [field]: null }, { target: options.target });
  if (!hasAuthCredentials()) return null;
  const target = options.target
    ? formatAccountConfigTarget(options.target)
    : "";
  const pending = entry.pending.get(target);
  // What was changed before the reset is sent first, so the reset wins.
  if (pending) await flushPending(entry, pending);
  const outcome = await send(
    entry,
    target,
    { method: "DELETE" },
    { retry: true },
  );
  return outcome === CONFLICT_UNRESOLVED ? null : outcome;
}

/** Sends every queued patch of every key now — before a profile or company switch. */
export async function flushAccountConfigs(): Promise<void> {
  const runs: Promise<void>[] = [];
  for (const entry of entries.values()) {
    for (const pending of entry.pending.values())
      runs.push(flushPending(entry, pending));
  }
  await Promise.all(runs);
}

/**
 * A tab that closes within the debounce would drop the last change. Only one
 * request per target can leave a closing page: the ones after it would wait
 * for its answer, which a closed page never gets. So on `pagehide` the first
 * queued patch of a target leaves by the normal path — `If-Match`, in order —
 * with `keepalive`, when nothing is in flight for the target and it needs no
 * version read first. Everything else (the patches after it: a removal and a
 * following object do not combine into one merge patch; all of them while a
 * write is in flight) is kept in order for the next start, which sends it after
 * master's first answer, each only with a version read. Back from the
 * back/forward cache (`pageshow`), the tab takes them up itself.
 */
function flushOnPageHide(): void {
  for (const entry of entries.values()) {
    const own = defaultTargetOf(entry);
    const kept: KeptWrite[] = [];
    for (const pending of entry.pending.values()) {
      if (pending.timer) {
        clearTimeout(pending.timer);
        pending.timer = null;
      }
      if (!pending.batches.length) continue;
      if (!pending.running && !pending.batches[0]!.retained)
        void flushPending(entry, pending, true);
      // The view is left as it is: the boot cache keeps showing these changes.
      for (const batch of pending.batches.splice(0)) {
        kept.push({ target: pending.target || own, patch: batch.patch });
        for (const waiter of batch.waiters) waiter(null);
      }
    }
    keepWrites(entry.key, kept);
  }
}

/**
 * Queues the changes a closed tab kept for this key — onto the default
 * target when they were meant for it — as kept-back changes: each is sent
 * only with a version read (`If-Match`), in order.
 */
function resumeKeptWrites(entry: ConfigEntry): void {
  entry.queueResumed = true;
  if (!hasAuthCredentials()) return;
  const writes = takeKeptWrites(entry.key);
  if (!writes.length) return;
  const own = defaultTargetOf(entry);
  const touched = new Set<PendingTarget>();
  for (const write of writes) {
    const pending = pendingFor(entry, write.target === own ? "" : write.target);
    pending.batches.push({ patch: write.patch, waiters: [], retained: true });
    touched.add(pending);
  }
  installPageHideFlush();
  for (const pending of touched) schedule(entry, pending, true);
  recompute(entry);
}

function resumeOnPageShow(): void {
  for (const entry of entries.values()) {
    entry.queueResumed = false;
    if (entry.loaded.value) resumeKeptWrites(entry);
  }
}

function installPageHideFlush(): void {
  if (pageHideInstalled || typeof window === "undefined") return;
  pageHideInstalled = true;
  window.addEventListener("pagehide", flushOnPageHide);
  window.addEventListener("pageshow", resumeOnPageShow);
}

/* ── Composable ────────────────────────────────────────────────────────── */

/**
 * The shared state of one config key. Any number of callers may hold a
 * handle; they all see one store. Safe outside components (no lifecycle
 * hooks) — the shell holds its keys at module level.
 */
export function useAccountConfig<T = JsonObject>(
  key: string,
  options: UseAccountConfigOptions<T> = {},
): AccountConfigHandle<T> {
  const entry = entryFor(key);
  const fallback = options.fallback;
  // A key loaded before anybody wanted its layers is read once more with them.
  const needsLayers =
    options.layers === true && !entry.wantsLayers && entry.loaded.value;
  if (options.layers) entry.wantsLayers = true;
  if (
    options.immediate !== false &&
    (!entry.loaded.value || needsLayers) &&
    entry.reads === 0 &&
    hasAuthCredentials()
  ) {
    queueLoad(key);
  }
  const effective = computed<T>(() =>
    fallback === undefined
      ? (entry.effective.value as T)
      : (mergeJson(
          stripNulls(cloneJson(fallback)),
          entry.effective.value,
        ) as T),
  );
  return {
    key,
    effective,
    enforced: entry.enforced,
    version: entry.version,
    target: entry.target,
    layers: entry.layers,
    loaded: entry.loaded,
    loading: entry.loading,
    error: entry.error,
    patch: (partial, patchOptions) =>
      queuePatch(
        entry,
        partial as JsonObject,
        patchOptions,
      ) as Promise<AccountConfigResult<T> | null>,
    reset: (field, resetOptions) =>
      resetEntry(
        entry,
        field,
        resetOptions,
      ) as Promise<AccountConfigResult<T> | null>,
    reload: async () => {
      await loadEntries([entry]);
    },
    flush: async () => {
      await Promise.all(
        [...entry.pending.values()].map((pending) =>
          flushPending(entry, pending),
        ),
      );
    },
  };
}

/** Test seam: make a key look as if master had answered `result` (queued writes are dropped). */
export function setAccountConfigForTests(
  key: string,
  effective: JsonObject,
  result: Partial<Omit<AccountConfigResult, "key" | "effective">> = {},
): void {
  const entry = entryFor(key);
  for (const pending of entry.pending.values()) {
    if (pending.timer) clearTimeout(pending.timer);
  }
  entry.pending.clear();
  entry.targetVersions.clear();
  entry.layerWrites.clear();
  entry.serverLayers = null;
  entry.layersRead = false;
  entry.layers.value = null;
  entry.error.value = null;
  applyServerResult(entry, {
    key,
    effective: cloneJson(effective),
    version: result.version ?? 0,
    target: result.target ?? null,
    enforced: result.enforced ?? [],
    ...(result.layers ? { layers: result.layers } : {}),
  });
}

/** Test seam: forget every key, timer and queued write. */
export function resetAccountConfigsForTests(): void {
  for (const entry of entries.values()) {
    for (const pending of entry.pending.values()) {
      if (pending.timer) clearTimeout(pending.timer);
    }
  }
  entries.clear();
  queuedLoads = new Set();
  queuedLoadScheduled = false;
  stampCounter = 0;
  windowsChannel?.close();
  windowsChannel = null;
  if (pageHideInstalled && typeof window !== "undefined") {
    window.removeEventListener("pagehide", flushOnPageHide);
    window.removeEventListener("pageshow", resumeOnPageShow);
  }
  pageHideInstalled = false;
}
